import type {
  DomainSelectDto,
  EffectSelectDto,
  MissionAssessmentEffectAiGenerationDataDto,
  MissionAssessmentEffectDataDto,
  TargetSystemSelectDto,
  UseSelectDto,
} from '@@types/api.types';
import { BadgeList } from '@components/shared/badge-list.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { ModelDisplay } from '@components/shared/form-suggestion.component';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { formatProper } from '@utils/text.util';
import { CheckIcon, XIcon } from 'lucide-react';
import React from 'react';
import {
  getEffectSuggestionCatalogItems,
  getEffectSuggestionCatalogName,
} from './mission-assessment-effect-suggestion.util';

type MissionAssessmentEffectSuggestionProps = {
  className?: string;
  data: MissionAssessmentEffectAiGenerationDataDto | null;
  domains: DomainSelectDto[];
  effects: EffectSelectDto[];
  /**
   * Reserved for future streaming UI. Today the parent gates rendering behind its own loading flag.
   */
  isGenerating: boolean;
  model?: string;
  onAccept: () => void;
  onReject: () => void;
  targetSystems: TargetSystemSelectDto[];
  uses: UseSelectDto[];
};

type MissionAssessmentEffectSuggestionFieldsProps = {
  data: MissionAssessmentEffectAiGenerationDataDto;
  domains: DomainSelectDto[];
  effects: EffectSelectDto[];
  targetSystems: TargetSystemSelectDto[];
  uses: UseSelectDto[];
};

type SuggestionFieldProps = {
  className?: string;
  expandable?: boolean;
  label: string;
  value: number | string | undefined;
};

type SuggestionBadgeFieldProps = {
  items: Array<{ id: string; name: string }>;
  label: string;
};

const formatCost = (cost?: { amount: number; currency: string }): string | undefined => {
  if (!cost) return undefined;

  return `${cost.currency} ${cost.amount.toLocaleString()}`;
};

const formatTime = (time?: { unit: string; value: number }): string | undefined => {
  if (!time) return undefined;

  return `${time.value.toLocaleString()} ${formatProper(time.unit)}`;
};

const SuggestionField: React.FC<SuggestionFieldProps> = (props) => {
  const { className, expandable = false, label, value } = props;

  if (value === undefined || value === '') return null;

  return (
    <div className={cn('p-3 border border-gray-200 rounded-lg', className)}>
      <div className="text-xs font-medium text-gray-500 mb-2">{label}</div>
      {expandable && typeof value === 'string' ? (
        <ExpandableText
          className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap"
          maxLines={3}
          text={value}
        />
      ) : (
        <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">{value}</div>
      )}
    </div>
  );
};

const SuggestionBadgeField: React.FC<SuggestionBadgeFieldProps> = (props) => {
  const { items, label } = props;

  if (items.length === 0) return null;

  return (
    <div className="p-3 border border-gray-200 rounded-lg">
      <div className="text-xs font-medium text-gray-500 mb-2">{label}</div>
      <BadgeList
        badgeVariant="outline"
        items={items}
        limit={6}
        size="sm"
      />
    </div>
  );
};

const SuggestionPhaseUnitRow: React.FC<{ data: MissionAssessmentEffectDataDto }> = (props) => {
  const { data } = props;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <SuggestionField
        label="Unit"
        value={data.unit}
      />
      <SuggestionField
        label="Minimum Phase"
        value={data.minPhase}
      />
      <SuggestionField
        label="Maximum Phase"
        value={data.maxPhase}
      />
    </div>
  );
};

const SuggestionCostRow: React.FC<{ data: MissionAssessmentEffectDataDto }> = (props) => {
  const { data } = props;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <SuggestionField
        label="Cost"
        value={formatCost(data.cost)}
      />
      <SuggestionField
        label="Cost to Replace"
        value={formatCost(data.costToReplace)}
      />
      <SuggestionField
        label="Personnel Required"
        value={data.personnelRequired}
      />
    </div>
  );
};

const SuggestionTimeRow: React.FC<{ data: MissionAssessmentEffectDataDto }> = (props) => {
  const { data } = props;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SuggestionField
        label="Time to Deploy"
        value={formatTime(data.timeToDeploy)}
      />
      <SuggestionField
        label="Time to Replace"
        value={formatTime(data.timeToReplace)}
      />
    </div>
  );
};

/**
 * Tone-neutral Effect field grid shared by the inline suggestion card and the
 * fullscreen chat artifact panel.
 */
export const MissionAssessmentEffectSuggestionFields: React.FC<MissionAssessmentEffectSuggestionFieldsProps> = (
  props,
) => {
  const { data, domains, effects, targetSystems, uses } = props;

  const catalogs = React.useMemo(
    () => ({
      domains,
      effects,
      targetSystems,
      uses,
    }),
    [domains, effects, targetSystems, uses],
  );

  const secondaryEffectItems = getEffectSuggestionCatalogItems({
    catalogs,
    ids: data.secondaryEffects,
    kind: 'effect',
  });
  const targetSystemItems = getEffectSuggestionCatalogItems({
    catalogs,
    ids: data.targetSystems,
    kind: 'targetSystem',
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <SuggestionField
          label="Effect"
          value={getEffectSuggestionCatalogName({ catalogs, id: data.effectId, kind: 'effect' })}
        />
        <SuggestionField
          label="Use"
          value={getEffectSuggestionCatalogName({ catalogs, id: data.useId, kind: 'use' })}
        />
        <SuggestionField
          label="Domain"
          value={getEffectSuggestionCatalogName({ catalogs, id: data.domainId, kind: 'domain' })}
        />
      </div>

      <SuggestionField
        expandable
        label="Characteristics & Measures"
        value={data.data.description}
      />

      <SuggestionField
        expandable
        label="Measures of Performance"
        value={data.data.measuresOfPerformance}
      />

      <SuggestionField
        expandable
        label="Measures of Impact/Effectiveness"
        value={data.data.measureOfImpact}
      />

      <SuggestionPhaseUnitRow data={data.data} />
      <SuggestionCostRow data={data.data} />
      <SuggestionTimeRow data={data.data} />

      <SuggestionBadgeField
        items={secondaryEffectItems}
        label="Secondary Effects"
      />

      <SuggestionBadgeField
        items={targetSystemItems}
        label="Target Systems"
      />
    </div>
  );
};

export const MissionAssessmentEffectSuggestion: React.FC<MissionAssessmentEffectSuggestionProps> = (props) => {
  const { className, data, domains, effects, model, onAccept, onReject, targetSystems, uses } = props;

  if (!data) return null;

  return (
    <div className={cn('p-4 bg-blue-50/50 border border-blue-300 rounded-lg shadow-sm', className)}>
      <MissionAssessmentEffectSuggestionFields
        data={data}
        domains={domains}
        effects={effects}
        targetSystems={targetSystems}
        uses={uses}
      />

      <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t">
        <div className="text-xs text-gray-500 font-medium">
          <ModelDisplay model={model} />
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={onReject}
            size="sm"
            variant="destructive"
          >
            <XIcon className="size-4" />
            Reject
          </Button>

          <Button
            onClick={onAccept}
            size="sm"
            variant="primary"
          >
            <CheckIcon className="size-4" />
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};
